import {
  Checkbox,
  ContextMenu,
  DragHandle as DragHandleIcon,
  Tooltip,
  useDraggable,
} from '@affine/component';
import { DocsService } from '@affine/core/modules/doc';
import { DocDisplayMetaService } from '@affine/core/modules/doc-display-meta';
import { WorkbenchLink } from '@affine/core/modules/workbench';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { useI18n } from '@affine/i18n';
import track from '@affine/track';
import {
  AutoTidyUpIcon,
  PropertyIcon,
  ResizeTidyUpIcon,
} from '@blocksuite/icons/rc';
import { DocsSearchService } from '@affine/core/modules/docs-search';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { toArrayBuffer } from '@affine/core/utils/array-buffer';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import {
  type HTMLProps,
  memo,
  type ReactNode,
  type SVGProps,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { DocSummaryService } from '@affine/core/modules/doc-summary';
import { PagePreview } from '../../page-list/page-content-preview';
import { DocExplorerContext } from '../context';
import { quickActions } from '../quick-actions.constants';
import * as styles from './doc-list-item.css';
import { MoreMenuButton, MoreMenuContent } from './more-menu';
import { CardViewProperties, ListViewProperties } from './properties';

export type DocListItemView = 'card' | 'list' | 'grid' | 'masonry';

const HorizontalCardsIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <rect
      x="2"
      y="2"
      width="12"
      height="2"
      rx="0.5"
      stroke="currentColor"
      strokeWidth="1.2"
    />
    <rect
      x="2"
      y="7"
      width="12"
      height="2"
      rx="0.5"
      stroke="currentColor"
      strokeWidth="1.2"
    />
    <rect
      x="2"
      y="12"
      width="12"
      height="2"
      rx="0.5"
      stroke="currentColor"
      strokeWidth="1.2"
    />
  </svg>
);

export const DocListViewIcon = ({
  view,
  ...props
}: { view: DocListItemView } & SVGProps<SVGSVGElement>) => {
  const Component = {
    card: HorizontalCardsIcon,
    list: PropertyIcon,
    grid: ResizeTidyUpIcon,
    masonry: AutoTidyUpIcon,
  }[view];

  return <Component {...props} />;
};

export interface DocListItemProps {
  docId: string;
  groupId: string;
}

class MixId {
  static connector = '||';
  static create(groupId: string, docId: string) {
    return `${groupId}${this.connector}${docId}`;
  }
  static parse(mixId: string) {
    if (!mixId) {
      return { groupId: null, docId: null };
    }
    const [groupId, docId] = mixId.split(this.connector);
    return { groupId, docId };
  }
}
export const DocListItem = ({ ...props }: DocListItemProps) => {
  const contextValue = useContext(DocExplorerContext);
  const view = useLiveData(contextValue.view$) ?? 'list';
  const groups = useLiveData(contextValue.groups$);
  const selectMode = useLiveData(contextValue.selectMode$);
  const selectedDocIds = useLiveData(contextValue.selectedDocIds$);
  const prevCheckAnchorId = useLiveData(contextValue.prevCheckAnchorId$);

  const handleMultiSelect = useCallback(
    (prevCursor: string, currCursor: string) => {
      const flattenList = groups.flatMap(group =>
        group.items.map(docId => MixId.create(group.key, docId))
      );

      const prev = contextValue.selectedDocIds$?.value ?? [];
      const prevIndex = flattenList.indexOf(prevCursor);
      const currIndex = flattenList.indexOf(currCursor);

      const lowerIndex = Math.min(prevIndex, currIndex);
      const upperIndex = Math.max(prevIndex, currIndex);

      const resSet = new Set(prev);
      const handledSet = new Set<string>();
      for (let i = lowerIndex; i <= upperIndex; i++) {
        const mixId = flattenList[i];
        const { groupId, docId } = MixId.parse(mixId);
        if (groupId === null || docId === null) {
          continue;
        }
        if (handledSet.has(docId) || mixId === prevCursor) {
          continue;
        }
        if (resSet.has(docId)) {
          resSet.delete(docId);
        } else {
          resSet.add(docId);
        }
        handledSet.add(docId);
      }

      contextValue.selectedDocIds$?.next(Array.from(resSet));
      contextValue.prevCheckAnchorId$?.next(currCursor);
    },
    [contextValue, groups]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<Element>) => {
      const { docId, groupId } = props;
      const currCursor = MixId.create(groupId, docId);
      if (selectMode || e.shiftKey) {
        e.preventDefault();
      }

      if (selectMode) {
        if (e.shiftKey && prevCheckAnchorId) {
          // do multi select
          handleMultiSelect(prevCheckAnchorId, currCursor);
        } else {
          contextValue.selectedDocIds$?.next(
            contextValue.selectedDocIds$.value.includes(docId)
              ? contextValue.selectedDocIds$.value.filter(id => id !== docId)
              : [...contextValue.selectedDocIds$.value, docId]
          );
          contextValue.prevCheckAnchorId$?.next(currCursor);
        }
      } else {
        if (e.shiftKey) {
          contextValue.selectMode$?.next(true);
          contextValue.selectedDocIds$?.next([docId]);
          contextValue.prevCheckAnchorId$?.next(currCursor);
          return;
        } else {
          // as link
          track.allDocs.list.doc.openDoc();
          return;
        }
      }
    },
    [contextValue, handleMultiSelect, prevCheckAnchorId, props, selectMode]
  );

  const { dragRef, CustomDragPreview } = useDraggable<AffineDNDData>(
    () => ({
      canDrag: true,
      data: {
        entity: {
          type: 'doc',
          id: props.docId as string,
        },
        from: {
          at: 'all-docs:list',
        },
      },
    }),
    [props.docId]
  );

  return (
    <>
      <WorkbenchLink
        ref={dragRef}
        draggable={false}
        to={`/${props.docId}`}
        onClick={handleClick}
        data-selected={selectedDocIds.includes(props.docId)}
        data-view={view}
        className={styles.root}
        data-testid={`doc-list-item`}
        data-doc-id={props.docId}
      >
        {view === 'list' ? (
          <ListViewDoc {...props} />
        ) : view === 'card' ? (
          <HorizontalCardViewDoc {...props} />
        ) : (
          <CardViewDoc {...props} />
        )}
      </WorkbenchLink>
      <CustomDragPreview>
        <div className={styles.dragPreview}>
          <RawDocIcon id={props.docId} className={styles.dragPreviewIcon} />
          <RawDocTitle id={props.docId} />
        </div>
      </CustomDragPreview>
    </>
  );
};

const RawDocIcon = memo(function RawDocIcon({
  id,
  ...props
}: HTMLProps<SVGSVGElement>) {
  const docDisplayMetaService = useService(DocDisplayMetaService);
  const Icon = useLiveData(id ? docDisplayMetaService.icon$(id) : null);
  return <Icon {...props} />;
});
const RawDocTitle = memo(function RawDocTitle({ id }: { id: string }) {
  const docDisplayMetaService = useService(DocDisplayMetaService);
  const title = useLiveData(docDisplayMetaService.title$(id));
  return title;
});
function renderMarkdownInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // Pattern matches: **bold**, _italic_, `code`, ~~strike~~, <u>underline</u>
  const regex =
    /(\*\*(.+?)\*\*)|(_(.+?)_)|(~~(.+?)~~)|(`(.+?)`)|(<u>(.+?)<\/u>)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    if (match[1]) {
      // **bold**
      nodes.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[3]) {
      // _italic_
      nodes.push(<em key={key++}>{match[4]}</em>);
    } else if (match[5]) {
      // ~~strike~~
      nodes.push(<s key={key++}>{match[6]}</s>);
    } else if (match[7]) {
      // `code`
      nodes.push(
        <code key={key++} className={styles.mdInlineCode}>
          {match[8]}
        </code>
      );
    } else if (match[9]) {
      // <u>underline</u>
      nodes.push(<u key={key++}>{match[10]}</u>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
}

function renderMarkdownPreview(markdown: string): ReactNode {
  const lines = markdown.split('\n');
  return lines.map((line, i) => {
    // Headings
    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      return (
        <div key={i} className={styles.mdHeading}>
          {renderMarkdownInline(headingMatch[2])}
        </div>
      );
    }
    // Blockquote
    if (line.startsWith('> ')) {
      return (
        <div key={i} className={styles.mdQuote}>
          {renderMarkdownInline(line.slice(2))}
        </div>
      );
    }
    // Code block (```lang ... ```) - handled as preformatted
    if (line.startsWith('```')) {
      return null; // skip code fence markers
    }
    // Checkbox list
    const checkboxMatch = line.match(/^- \[([ x])\]\s+(.*)$/);
    if (checkboxMatch) {
      return (
        <div key={i} className={styles.mdListItem}>
          <span className={styles.mdBullet}>
            {checkboxMatch[1] === 'x' ? '☑' : '☐'}
          </span>
          {renderMarkdownInline(checkboxMatch[2])}
        </div>
      );
    }
    // Unordered list
    if (line.startsWith('* ') || line.startsWith('- ')) {
      return (
        <div key={i} className={styles.mdListItem}>
          <span className={styles.mdBullet}>•</span>
          {renderMarkdownInline(line.slice(2))}
        </div>
      );
    }
    // Ordered list
    const orderedMatch = line.match(/^(\d+)\.\s+(.*)$/);
    if (orderedMatch) {
      return (
        <div key={i} className={styles.mdListItem}>
          <span className={styles.mdBullet}>{orderedMatch[1]}.</span>
          {renderMarkdownInline(orderedMatch[2])}
        </div>
      );
    }
    // Regular paragraph
    if (line.trim()) {
      return (
        <div key={i} className={styles.mdParagraph}>
          {renderMarkdownInline(line)}
        </div>
      );
    }
    return null;
  });
}

const RawDocPreview = memo(function RawDocPreview({
  id,
  loading,
}: {
  id: string;
  loading?: ReactNode;
}) {
  const docSummary = useService(DocSummaryService);
  const markdownSummary = useLiveData(
    useMemo(
      () => LiveData.from(docSummary.watchDocMarkdownSummary(id), null),
      [docSummary, id]
    )
  );

  if (markdownSummary === null) {
    return loading ?? null;
  }

  if (markdownSummary) {
    return <>{renderMarkdownPreview(markdownSummary)}</>;
  }

  // Fall back to plain text summary
  return <PagePreview pageId={id} fallback={loading} />;
});
const DragHandle = memo(function DragHandle({
  id,
  ...props
}: HTMLProps<HTMLDivElement>) {
  const contextValue = useContext(DocExplorerContext);
  const selectMode = useLiveData(contextValue.selectMode$);
  const showDragHandle = useLiveData(contextValue.showDragHandle$);

  if (selectMode || !id || !showDragHandle) {
    return null;
  }

  return (
    <div {...props}>
      <DragHandleIcon />
    </div>
  );
});
const Select = memo(function Select({
  id,
  ...props
}: HTMLProps<HTMLDivElement>) {
  const contextValue = useContext(DocExplorerContext);
  const selectMode = useLiveData(contextValue.selectMode$);
  const selectedDocIds = useLiveData(contextValue.selectedDocIds$);

  const handleSelectChange = useCallback(() => {
    id && contextValue.selectedDocIds$?.next([id]);
  }, [id, contextValue]);

  if (!id) {
    return null;
  }

  return (
    <div
      data-select-mode={selectMode}
      data-testid={`doc-list-item-select`}
      {...props}
    >
      <Checkbox
        checked={selectedDocIds.includes(id)}
        onChange={handleSelectChange}
      />
    </div>
  );
});
// Different with RawDocIcon, refer to `ExplorerDisplayPreference.showDocIcon`
const DocIcon = memo(function DocIcon({
  id,
  ...props
}: HTMLProps<HTMLDivElement>) {
  const contextValue = useContext(DocExplorerContext);
  const showDocIcon = useLiveData(contextValue.showDocIcon$);
  if (!showDocIcon) {
    return null;
  }
  return (
    <div {...props}>
      <RawDocIcon id={id} />
    </div>
  );
});
const DocTitle = memo(function DocTitle({
  id,
  hideIfUntitled,
  ...props
}: HTMLProps<HTMLDivElement> & { hideIfUntitled?: boolean }) {
  const docsService = useService(DocsService);
  const doc = useLiveData(id ? docsService.list.doc$(id) : null);
  const rawTitle = useLiveData(doc ? doc.title$ : null);

  if (!id) return null;
  if (hideIfUntitled && !rawTitle) return null;

  return (
    <div {...props}>
      <RawDocTitle id={id} />
    </div>
  );
});
const DocPreview = memo(function DocPreview({
  id,
  loading,
  ...props
}: HTMLProps<HTMLDivElement> & { loading?: ReactNode }) {
  const contextValue = useContext(DocExplorerContext);
  const showDocPreview = useLiveData(contextValue.showDocPreview$);

  if (!id || !showDocPreview) return null;

  return (
    <div {...props}>
      <RawDocPreview id={id} loading={loading} />
    </div>
  );
});

function useDocFirstImage(docId: string) {
  const workspaceService = useService(WorkspaceService);
  const docsSearchService = useService(DocsSearchService);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;

    const fetchImage = async () => {
      try {
        // Fetch the first ~20 blocks to check if an image appears early
        const result = await docsSearchService.indexer.search('block', {
          type: 'match',
          field: 'docId',
          match: docId,
        }, {
          fields: ['flavour', 'content', 'blob'] as const,
          pagination: { limit: 20 },
        });

        if (revoked || result.nodes.length === 0) return;

        // Walk blocks in order, counting text tokens.
        // Only use the image if it appears before 100 tokens of text.
        let tokenCount = 0;
        let imageBlobId: string | null = null;

        for (const node of result.nodes) {
          const flavour = typeof node.fields.flavour === 'string'
            ? node.fields.flavour
            : node.fields.flavour[0];

          if (flavour === 'affine:image') {
            const blob = typeof node.fields.blob === 'string'
              ? node.fields.blob
              : node.fields.blob?.[0];
            if (blob) {
              imageBlobId = blob;
              break; // found an early image
            }
          }

          // Count tokens from content blocks (skip containers like note/page)
          if (flavour === 'affine:page' || flavour === 'affine:note') continue;

          const content = typeof node.fields.content === 'string'
            ? node.fields.content
            : node.fields.content?.[0] || '';
          if (content) {
            tokenCount += content.split(/\s+/).filter(Boolean).length;
          }
          if (tokenCount >= 100) break; // past threshold, no early image
        }

        if (!imageBlobId || revoked) return;

        const record = await workspaceService.workspace.engine.blob.get(imageBlobId);
        if (!record || revoked) return;

        const blob = new Blob([toArrayBuffer(record.data)], {
          type: record.mime,
        });
        objectUrl = URL.createObjectURL(blob);
        if (!revoked) {
          setImageUrl(objectUrl);
        }
      } catch {
        // indexer might not be ready yet, silently ignore
      }
    };

    fetchImage();

    return () => {
      revoked = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [docId, docsSearchService, workspaceService]);

  return imageUrl;
}

const DocThumbnail = memo(function DocThumbnail({
  id,
  ...props
}: HTMLProps<HTMLDivElement>) {
  const imageUrl = useDocFirstImage(id ?? '');

  if (!id || !imageUrl) return null;

  return (
    <div {...props}>
      <img
        src={imageUrl}
        alt=""
        className={styles.thumbnailImage}
      />
    </div>
  );
});

const listMoreMenuContentOptions = {
  side: 'bottom',
  align: 'end',
  sideOffset: 12,
  alignOffset: -4,
} as const;
export const ListViewDoc = ({ docId }: DocListItemProps) => {
  const t = useI18n();
  const docsService = useService(DocsService);
  const doc = useLiveData(docsService.list.doc$(docId));
  const contextValue = useContext(DocExplorerContext);
  const showMoreOperation = useLiveData(contextValue.showMoreOperation$);

  if (!doc) {
    return null;
  }

  return (
    <ContextMenu
      asChild
      disabled={!showMoreOperation}
      items={<MoreMenuContent docId={docId} />}
    >
      <li className={styles.listViewRoot}>
        <DragHandle id={docId} className={styles.listDragHandle} />
        <Select id={docId} className={styles.listSelect} />
        <DocIcon id={docId} className={styles.listIcon} />
        <div className={styles.listBrief}>
          <DocTitle
            id={docId}
            className={styles.listTitle}
            data-testid="doc-list-item-title"
          />
          <DocPreview id={docId} className={styles.listPreview} />
        </div>
        <ListViewProperties docId={docId} />
        {quickActions.map(action => {
          return (
            <Tooltip key={action.key} content={t.t(action.name)}>
              <action.Component doc={doc} />
            </Tooltip>
          );
        })}
        <MoreMenuButton
          docId={docId}
          contentOptions={listMoreMenuContentOptions}
        />
      </li>
    </ContextMenu>
  );
};

const cardMoreMenuContentOptions = {
  side: 'bottom',
  align: 'end',
  sideOffset: 12,
  alignOffset: -4,
} as const;

export const CardViewDoc = ({ docId }: DocListItemProps) => {
  const t = useI18n();
  const contextValue = useContext(DocExplorerContext);
  const selectMode = useLiveData(contextValue.selectMode$);
  const docsService = useService(DocsService);
  const doc = useLiveData(docsService.list.doc$(docId));
  const showMoreOperation = useLiveData(contextValue.showMoreOperation$);

  if (!doc) {
    return null;
  }

  return (
    <ContextMenu
      asChild
      disabled={!showMoreOperation}
      items={<MoreMenuContent docId={docId} />}
    >
      <li className={styles.cardViewRoot}>
        <DragHandle id={docId} className={styles.cardDragHandle} />
        <header className={styles.cardViewHeader}>
          <DocIcon id={docId} className={styles.cardViewIcon} />
          <DocTitle
            id={docId}
            className={styles.cardViewTitle}
            data-testid="doc-list-item-title"
          />
          {quickActions.map(action => {
            return (
              <Tooltip key={action.key} content={t.t(action.name)}>
                <action.Component size="16" doc={doc} />
              </Tooltip>
            );
          })}
          {selectMode ? (
            <Select id={docId} className={styles.cardViewCheckbox} />
          ) : (
            <MoreMenuButton
              docId={docId}
              contentOptions={cardMoreMenuContentOptions}
              iconProps={{ size: '16' }}
            />
          )}
        </header>
        <DocPreview id={docId} className={styles.cardPreviewContainer} />
        <DocThumbnail id={docId} className={styles.cardThumbnailContainer} />
        <CardViewProperties docId={docId} />
      </li>
    </ContextMenu>
  );
};

const horizontalCardMoreMenuContentOptions = {
  side: 'bottom',
  align: 'end',
  sideOffset: 12,
  alignOffset: -4,
} as const;

export const HorizontalCardViewDoc = ({ docId }: DocListItemProps) => {
  const t = useI18n();
  const contextValue = useContext(DocExplorerContext);
  const selectMode = useLiveData(contextValue.selectMode$);
  const docsService = useService(DocsService);
  const doc = useLiveData(docsService.list.doc$(docId));
  const showMoreOperation = useLiveData(contextValue.showMoreOperation$);

  if (!doc) {
    return null;
  }

  return (
    <ContextMenu
      asChild
      disabled={!showMoreOperation}
      items={<MoreMenuContent docId={docId} />}
    >
      <li className={styles.horizontalCardViewRoot}>
        <DragHandle id={docId} className={styles.horizontalCardDragHandle} />
        <div className={styles.horizontalCardHeader}>
          <DocTitle
            id={docId}
            hideIfUntitled
            className={styles.horizontalCardTitle}
            data-testid="doc-list-item-title"
          />
          {quickActions.map(action => {
            return (
              <Tooltip key={action.key} content={t.t(action.name)}>
                <action.Component size="16" doc={doc} />
              </Tooltip>
            );
          })}
          {selectMode ? (
            <Select id={docId} className={styles.cardViewCheckbox} />
          ) : (
            <MoreMenuButton
              docId={docId}
              contentOptions={horizontalCardMoreMenuContentOptions}
              iconProps={{ size: '16' }}
            />
          )}
        </div>
        <div className={styles.horizontalCardBody}>
          <DocPreview
            id={docId}
            className={styles.horizontalCardPreview}
          />
          <DocThumbnail
            id={docId}
            className={styles.horizontalCardThumbnail}
          />
        </div>
        <CardViewProperties docId={docId} />
      </li>
    </ContextMenu>
  );
};
